import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  getFeaturesForRoute,
  getPreferenceGroup,
} from 'lumen/utils/constants/navigation';

interface NavigationItem extends Option {
  active: boolean;
  featureName?: string | string[];
  photonUrl?: string;
  queryParams?: Record<string, unknown>;
  route: string;
}

interface NavigationGroup {
  children: NavigationItem[];
  dataTestId?: string;
  key: string;
}

interface HotelAndMarketNavigationNavigationMainDropdownSignature {
  Args: {
    group: NavigationGroup;
    isSmall?: boolean;
  };
}

export default class HotelAndMarketNavigationNavigationMainDropdown extends Component<HotelAndMarketNavigationNavigationMainDropdownSignature> {
  @service declare featureToggle: Services['feature-toggle'];
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];

  get isNavigationTriggerActive(): boolean {
    return this.args.group.children.some((item) => item.active);
  }

  get value(): NavigationItem | { label: string } {
    return (
      this.args.group.children.find((item) => item.active) ?? {
        label: this.args.group.key,
      }
    );
  }

  @action
  navigateTo(item: NavigationItem): void {
    if (item.photonUrl) {
      openUrl(item.photonUrl, true);
      return;
    }

    const queryParams = item.queryParams ?? {};

    if (
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW,
      ) ||
      !item.featureName
    ) {
      this.router.transitionTo(item.route, { queryParams });
      return;
    }

    // For grouped routes (e.g. pricing-manager / rates), resolve the
    // last-visited route and use the correct features for it
    let { route, featureName } = item;

    if (getPreferenceGroup(route)) {
      const persisted = this.navigation.getPersistedRoute(route);

      if (persisted && persisted !== route) {
        route = persisted;
        featureName = getFeaturesForRoute(persisted) ?? featureName;
      }
    }

    const subscriptionId = this.navigation.getSubscriptionId(
      featureName,
      route,
    );
    if (!subscriptionId) {
      this.router.transitionTo(route, { queryParams });
      return;
    }

    this.router.transitionTo(route, subscriptionId, { queryParams });
  }

  <template>
    <PrismComboSelectInput
      @onChange={{this.navigateTo}}
      @options={{@group.children}}
      @unsafe_isNavigationTrigger={{true}}
      @unsafe_isNavigationTriggerActive={{this.isNavigationTriggerActive}}
      @unsafe_isNavigationTriggerSmall={{@isSmall}}
      {{! @glint-expect-error: value can be a fallback with only label when no item is active }}
      @value={{this.value}}
      data-test-id={{@group.dataTestId}}
    />
  </template>
}
